import React, { useEffect, useMemo, useState } from "react";
import { Timer } from "lucide-react";
import { eventConfig } from "@/config/eventConfig";

const pad = (n) => String(n).padStart(2, "0");

export function useCountdown(targetISO) {
  const target = useMemo(() => new Date(targetISO).getTime(), [targetISO]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, target - now);
  return {
    done: diff <= 0,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

const Cell = ({ value, label }) => (
  <div className="flex flex-col items-center justify-center min-w-[62px] sm:min-w-[72px] rounded-xl bg-white/10 border border-white/15 px-3 py-2.5 backdrop-blur-sm">
    <span className="text-2xl sm:text-3xl font-bold tabular-nums leading-none text-[#FFD16F]" data-testid={`countdown-${label.toLowerCase()}`}>
      {pad(value)}
    </span>
    <span className="mt-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-white/60">{label}</span>
  </div>
);

/**
 * Countdown to the seminar start (eventConfig.event.startISO).
 * variant: "block" (cards) | "compact" (inline text)
 */
export const Countdown = ({ variant = "block", label = "Seminar starts in", showLabel = true, className = "" }) => {
  const { days, hours, minutes, seconds, done } = useCountdown(eventConfig.event.startISO);

  if (variant === "compact") {
    return (
      <span
        data-testid="countdown-compact"
        className={`inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums text-white/90 ${className}`}
        aria-label={done ? "The seminar is starting now" : `Seminar starts in ${days} days ${hours} hours ${minutes} minutes`}
      >
        <Timer size={15} className="text-[#FFD16F]" aria-hidden="true" />
        {done ? (
          "Happening now"
        ) : (
          <>
            <span className="hidden md:inline text-white/60 font-medium">Starts in</span>
            {days}d <span className="text-white/40">:</span> {pad(hours)}h <span className="text-white/40">:</span> {pad(minutes)}m <span className="text-white/40">:</span> {pad(seconds)}s
          </>
        )}
      </span>
    );
  }

  return (
    <div data-testid="countdown-block" className={className}>
      {showLabel && (
        <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-white/70">
          <Timer size={14} className="text-[#FFD16F]" aria-hidden="true" />
          {done ? "The seminar is underway" : label}
        </p>
      )}
      {!done && (
        <div className="flex items-center gap-2 sm:gap-3">
          <Cell value={days} label="Days" />
          <span className="text-xl font-bold text-white/30" aria-hidden="true">:</span>
          <Cell value={hours} label="Hrs" />
          <span className="text-xl font-bold text-white/30" aria-hidden="true">:</span>
          <Cell value={minutes} label="Mins" />
          <span className="text-xl font-bold text-white/30" aria-hidden="true">:</span>
          <Cell value={seconds} label="Secs" />
        </div>
      )}
    </div>
  );
};

export default Countdown;
